<template>
  <div>
    <loading 
      :active.sync="loading"
      :can-cancel="false"
      background-color="#fff"
      :is-full-page="false">
    </loading>

    <div v-if="service" class="guarderia-container">
      <!-- Grid principal: Foto+Status y Bitácora -->
      <b-row class="guarderia-main-grid">
        <!-- Columna izquierda: Foto de mascota + Status en Vivo -->
        <b-col lg="5" md="12" class="mb-4 mb-lg-0">
          <div class="pet-status-card">
            <div class="pet-status-header">
              <h3 class="pet-status-title">{{ service.mascota }} • {{ service.tipo_servicio }}</h3>
              <b-button 
                variant="link" 
                @click="refreshService"
                :disabled="loading"
                size="sm"
                class="refresh-header-btn"
                title="Refrescar"
              >
                <CIcon name="cilReload" :class="{ 'spinning': loading }"/>
              </b-button>
            </div>
            <div class="pet-status-body">
              <!-- Información del Hospedador -->
              <div v-if="service.walkerName || service.walkerPicture" class="walker-info-section mb-3">
                <div class="walker-info-card">
                  <div class="walker-info-content">
                    <div class="walker-photo-container">
                      <img 
                        v-if="service.walkerPicture" 
                        :src="service.walkerPicture" 
                        alt="Foto del hospedador"
                        class="walker-photo"
                      />
                      <div v-else class="walker-photo-placeholder">
                        <CIcon name="cilUser" size="lg" class="text-muted"/>
                      </div>
                    </div>
                    <div class="walker-details">
                      <div class="walker-label">Hospedador</div>
                      <div class="walker-name">{{ service.walkerName || 'Sin nombre' }}</div>
                      <!-- Calificación del hospedador -->
                      <div v-if="service.walkerAvgScore && service.walkerTotalQualifications" class="walker-rating">
                        <div class="walker-rating-content">
                          <b-form-rating 
                            :value="service.walkerAvgScore" 
                            readonly 
                            variant="warning"
                            size="sm"
                            class="walker-rating-stars"
                            no-border
                          ></b-form-rating>
                          <span class="walker-rating-score">{{ formatScore(service.walkerAvgScore) }}</span>
                          <span class="walker-rating-count">({{ service.walkerTotalQualifications }} {{ service.walkerTotalQualifications === 1 ? 'calificación' : 'calificaciones' }})</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Foto de mascota -->
              <div class="pet-photo-section">
                <div v-if="petPhoto" class="pet-photo-container">
                  <img 
                    :src="petPhoto" 
                    alt="Foto de mascota"
                    class="pet-photo-img"
                  />
                  <div class="pet-photo-overlay">
                    <div class="pet-names">{{ service.mascota }}</div>
                  </div>
                </div>
                <div v-else class="pet-photo-placeholder">
                  <CIcon name="cilDog" size="2x" class="text-muted"/>
                  <p class="text-muted mt-2" style="font-size: 0.875rem;">Sin foto de mascota</p>
                </div>
              </div>

              <!-- Status en Vivo -->
              <div class="status-section mt-3">
                <div class="status-header-small">
                  <h4 class="status-title-small">Ubicación</h4>
                </div>
                <!-- Banner de status -->
                <div v-if="lastEvidence" class="status-banner-small">
                  <span class="status-emoji-small">🐶</span>
                  <span class="status-text-small">{{ lastEvidence.label || 'Sin actividad reciente' }}</span>
                </div>
                <div v-else class="status-banner-small">
                  <span class="status-emoji-small">🐶</span>
                  <span class="status-text-small">Durmiendo plácidamente</span>
                </div>

                <!-- Zona Segura -->
                <div class="safe-zone-section mt-2">
                  <div class="safe-zone-badge-small">
                    <CIcon name="cilCheckCircle" class="mr-1" style="font-size: 0.875rem;"/>
                    <span style="font-size: 0.875rem;">Zona Segura</span>
                  </div>
                  <div v-if="lastPositionDate" class="last-position-date mt-2">
                    <small class="text-muted">
                      <strong>Última ubicación:</strong> {{ formatDateShort(lastPositionDate) }}
                    </small>
                  </div>
                </div>

                <!-- Mapa -->
                <div class="map-container mt-2">
                  <gmap-map
                    v-if="mapCenter"
                    :center="mapCenter"
                    :zoom="16"
                    style="height: 250px; width: 100%; border-radius: 8px; overflow: hidden;"
                    :options="{
                      mapTypeControl: true,
                      mapTypeControlOptions: {
                        style: 1,
                        position: 3
                      },
                      zoomControl: true,
                      streetViewControl: false,
                      fullscreenControl: true
                    }"
                  >
                    <gmap-marker
                      :position="mapCenter"
                      :clickable="false"
                      :draggable="false"
                      title="Ubicación actual"
                      :icon="{
                        url: require('@/assets/images/huella24.png'),
                        scaledSize: { width: 30, height: 30 }
                      }"
                    ></gmap-marker>
                    <gmap-circle
                      :center="mapCenter"
                      :radius="100"
                      :options="{
                        fillColor: '#4caf50',
                        fillOpacity: 0.2,
                        strokeColor: '#4caf50',
                        strokeOpacity: 0.8,
                        strokeWeight: 2
                      }"
                    ></gmap-circle>
                  </gmap-map>
                  <div v-else class="map-placeholder-small">
                    <CIcon name="cilMap" size="2x" class="text-muted"/>
                    <p class="text-muted mt-2" style="font-size: 0.875rem;">Sin ubicación disponible</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-col>

        <!-- Columna derecha: Bitácora del Día -->
        <b-col lg="7" md="12">
          <div class="bitacora-card">
        <div class="bitacora-header">
          <h3 class="bitacora-title">Bitácora del Día</h3>
        </div>
        <div class="bitacora-body">
          <div v-if="bitacoraItems.length > 0" class="bitacora-timeline">
            <div 
              v-for="(item, index) in bitacoraItems" 
              :key="index"
              class="bitacora-item"
            >
              <div class="bitacora-icon" :class="item.iconClass">
                <CIcon :name="item.icon" />
              </div>
              <div class="bitacora-content">
                <div class="bitacora-time">{{ item.time }}</div>
                <div class="bitacora-label">
                  <span v-if="item.emoji" class="bitacora-emoji">{{ item.emoji }}</span>
                  <span v-if="item.isCheckIn" class="bitacora-checkin-link" @click="openCheckInDetail">
                    {{ item.label }}
                  </span>
                  <span v-else-if="item.hasPhoto" class="bitacora-photo-link" @click="openImageFullscreen(item.photoUrl, item.label)">
                    {{ item.label }}
                    <CIcon name="cilImage" class="ml-1" style="font-size: 0.875rem;"/>
                  </span>
                  <span v-else>{{ item.label }}</span>
                </div>
                <div v-if="item.videoLink" class="bitacora-link">
                  <a href="#" @click.prevent="openVideoFullscreen(item.videoUrl, item.label)">Ver Video</a>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="bitacora-empty">
            <p class="text-muted">No hay actividades registradas</p>
          </div>
        </div>
          </div>
        </b-col>
      </b-row>

      <!-- Galería de Hoy -->
      <div class="gallery-card mt-4">
        <div class="gallery-header">
          <h3 class="gallery-title">Galería de Hoy</h3>
        </div>
        <div class="gallery-body">
          <div v-if="galleryItems.length > 0" class="gallery-grid">
            <div 
              v-for="(item, index) in galleryItems" 
              :key="index"
              class="gallery-item"
              @click="item.isVideo ? openVideoFullscreen(item.video, item.label) : openImageFullscreen(item.photo, item.label)"
            >
              <img 
                v-if="!item.isVideo"
                :src="item.photo" 
                :alt="item.label"
                class="gallery-img"
              />
              <video 
                v-else
                :src="item.video" 
                class="gallery-img"
                muted
              ></video>
              <div v-if="item.isVideo" class="gallery-play-overlay">
                <CIcon name="cilMediaPlay" size="2x" class="text-white"/>
                <span class="gallery-video-label">Video</span>
              </div>
              <div class="gallery-overlay">
                <CIcon :name="item.isVideo ? 'cilMediaPlay' : 'cilZoom'" class="text-white"/>
              </div>
            </div>
          </div>
          <div v-else class="gallery-empty">
            <CIcon name="cilImage" size="3x" class="text-muted"/>
            <p class="text-muted mt-3">No hay fotos en la galería</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Detalle de Check-in -->
    <b-modal
      v-model="showCheckInDetailModal"
      title="Detalle de Check-in"
      size="lg"
      @hide="showCheckInDetailModal = false"
      header-class="checkin-modal-header"
      body-class="checkin-modal-body"
      ok-only
      ok-title="Cerrar"
      @ok="showCheckInDetailModal = false"
    >
      <div v-if="checkInEvidence" class="checkin-detail">
        <p class="text-muted mb-3">
          <strong>Fecha:</strong> {{ formatDate(checkInEvidence.created_at) }}
        </p>
        
        <div class="checkin-detail-items">
          <div 
            v-for="(item, index) in checkInEvidenceItems" 
            :key="index"
            class="checkin-detail-item mb-3"
          >
            <div class="checkin-detail-card">
              <div class="checkin-detail-content">
                <div class="checkin-detail-left">
                  <strong class="checkin-detail-label">{{ item.label || 'Sin etiqueta' }}</strong>
                </div>
                <div class="checkin-detail-right">
                  <div v-if="item.photo" class="checkin-detail-photo-thumbnail" @click="openImageFullscreen(item.photo, item.label)">
                    <img 
                      :src="item.photo" 
                      alt="Foto"
                      class="checkin-thumbnail-img"
                    />
                    <div class="checkin-thumbnail-overlay">
                      <CIcon name="cilZoom" class="text-white"/>
                    </div>
                  </div>
                  <div v-else-if="item.video" class="checkin-detail-photo-thumbnail" @click="openVideoFullscreen(item.video, item.label)">
                    <video 
                      :src="item.video" 
                      class="checkin-thumbnail-img"
                      muted
                    ></video>
                    <div class="checkin-thumbnail-overlay">
                      <CIcon name="cilMediaPlay" class="text-white"/>
                    </div>
                  </div>
                  <div v-else class="checkin-detail-no-photo">
                    <CIcon name="cilImage" class="text-muted"/>
                    <small class="text-muted d-block">Sin media</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-center py-4">
        <p class="text-muted">No hay información de check-in disponible</p>
      </div>
    </b-modal>

    <!-- Botón flotante estilo Android con speed dial -->
    <div v-if="service" class="fab-container">
      <!-- Overlay para cerrar al hacer clic fuera -->
      <div 
        v-if="fabMenuOpen" 
        class="fab-overlay"
        @click="fabMenuOpen = false">
      </div>
      
      <!-- Botones secundarios -->
      <transition-group name="fab-item" tag="div" class="fab-items">
        <div 
          v-if="fabMenuOpen && service.walkerPhone"
          key="whatsapp"
          class="fab-item"
          :style="{ bottom: getWhatsAppButtonBottom() }"
          @click="handleFabAction('whatsapp')">
          <div class="fab-label">Contactar al hospedador</div>
          <button 
            class="fab-button secondary"
            :style="{ backgroundColor: '#25D366' }"
            :disabled="loading">
            <CIcon name="cib-Whatsapp" width="20" style="color: #fff;"/>
          </button>
        </div>
        <div 
          v-if="fabMenuOpen"
          key="refrescar"
          class="fab-item"
          :style="{ bottom: getRefreshButtonBottom() }"
          @click="handleFabAction('refrescar')">
          <div class="fab-label">Refrescar</div>
          <button 
            class="fab-button secondary"
            :style="{ backgroundColor: '#6c757d' }"
            :disabled="loading">
            <CIcon name="cilReload" width="20" style="color: #fff;"/>
          </button>
        </div>
      </transition-group>
      
      <!-- Botón principal -->
      <button 
        class="fab-button primary"
        :style="{ backgroundColor: '#667eea', transform: fabMenuOpen ? 'rotate(45deg)' : 'rotate(0deg)' }"
        @click="toggleFabMenu"
        :disabled="loading">
        <CIcon name="cilOptions" width="24" style="color: #fff;"/>
      </button>
    </div>

  </div>
</template>

<script>
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import moment from 'moment';

export default {
  name: 'GuarderiaDetail',
  components: {
    Loading
  },
  data() {
    return {
      loading: true,
      service: null,
      mapCenter: null,
      lastPositionDate: null,
      petPhoto: null,
      lastEvidence: null,
      bitacoraItems: [],
      galleryItems: [],
      showCheckInDetailModal: false,
      fabMenuOpen: false
    }
  },
  created() {
    this.loadServiceData();
  },
  computed: {
    checkInEvidence() {
      if (!this.service || !this.service.evidencias) {
        return null;
      }
      return this.service.evidencias.find(ev => ev.check_in === true || ev.type === 'check_in') || null;
    },
    checkInEvidenceItems() {
      if (!this.checkInEvidence || !this.checkInEvidence.evidence) {
        return [];
      }
      return this.checkInEvidence.evidence.filter(item => item.label || item.photo || item.video);
    }
  },
  methods: {
    async loadServiceData() {
      this.loading = true;
      try {
        // Obtener serviceId de la query string
        const serviceId = this.$route.query.serviceId;
        if (!serviceId) {
          this.$bvToast.toast('ID de servicio no proporcionado', {
            title: 'Error',
            variant: 'danger',
            solid: true
          });
          this.loading = false;
          return;
        }

        // Llamar a la función getServiceByIdWithEvidence
        const getServiceByIdWithEvidence = this.$firebase.functions().httpsCallable('getServiceByIdWithEvidence');
        const payload = {
          serviceId: parseInt(serviceId)
        };

        const { data: response } = await getServiceByIdWithEvidence(payload);
        console.log('Service data:', response);
        
        if (response) {
          const serviceData = response.result || response;
          this.service = serviceData;
          this.processServiceData();
        } else {
          throw new Error('No se encontró información del servicio');
        }
      } catch (error) {
        console.error('Error al cargar datos del servicio:', error);
        this.$bvToast.toast('Error al cargar los datos del servicio', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.loading = false;
      }
    },
    async refreshService() {
      if (!this.service || !this.service.id) {
        return;
      }

      this.loading = true;
      try {
        const getServiceByIdWithEvidence = this.$firebase.functions().httpsCallable('getServiceByIdWithEvidence');
        const payload = {
          serviceId: this.service.id
        };

        const { data: response } = await getServiceByIdWithEvidence(payload);
        console.log('Servicio refrescado:', response);
        
        // Actualizar this.service con la respuesta
        if (response) {
          const serviceData = response.result || response;
          this.service = serviceData;
          this.processServiceData();
          this.$bvToast.toast('Servicio refrescado correctamente', {
            title: 'Éxito',
            variant: 'success',
            solid: true
          });
        }
      } catch (error) {
        console.error('Error al actualizar el servicio:', error);
        this.$bvToast.toast('Error al actualizar el servicio', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.loading = false;
      }
    },
    processServiceData() {
      if (!this.service) {
        return;
      }

      if (this.service.evidencias) {
      // 1. Obtener última posición GPS (type = position)
      const positionEvidence = this.service.evidencias
        .filter(ev => ev.type === 'position')
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
      
      if (positionEvidence && positionEvidence.evidence && positionEvidence.evidence[0]) {
        const pos = positionEvidence.evidence[0];
        if (pos.lat && pos.long) {
          this.mapCenter = {
            lat: pos.lat,
            lng: pos.long
          };
        }
        // Guardar fecha de última ubicación
        if (positionEvidence.updated_at) {
          this.lastPositionDate = positionEvidence.updated_at;
        } else if (positionEvidence.created_at) {
          this.lastPositionDate = positionEvidence.created_at;
        }
      }

      // 2. Obtener foto de mascota del check-in (type = check_in, label = mascota)
      const checkInEvidence = this.service.evidencias.find(ev => ev.check_in === true || ev.type === 'check_in');
      if (checkInEvidence && checkInEvidence.evidence) {
        const mascotaEvidence = checkInEvidence.evidence.find(e => e.label === 'mascota' && e.photo);
        if (mascotaEvidence && mascotaEvidence.photo) {
          this.petPhoto = mascotaEvidence.photo;
        }
      }

      // 3. Obtener último evidence (type = evidence) para status
      const evidenceList = this.service.evidencias
        .filter(ev => ev.type === 'evidence' || (ev.check_in === false && !ev.type))
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      if (evidenceList.length > 0 && evidenceList[0].evidence && evidenceList[0].evidence[0]) {
        this.lastEvidence = evidenceList[0].evidence[0];
      }

      // 5. Construir galería: evidencias con foto ordenadas asc por fecha
      this.buildGallery();
      }

      // 4. Construir bitácora: WFStates (iniciar/finalizar paseo, aplazado), check-in, evidencias
      this.buildBitacora();
    },
    buildBitacora() {
      this.bitacoraItems = [];
      const events = [];

      // 1. WFStates (estados 2 = Iniciar paseo, 34 = Finalizar, 134 = Aplazar por lluvia)
      if (this.service.WFStates && Array.isArray(this.service.WFStates)) {
        this.service.WFStates.forEach(state => {
          const date = state.dateTime ? new Date(state.dateTime) : null;
          if (date) {
            events.push({
              date,
              type: 'wfstate',
              stateId: state.stateId,
              stateName: state.stateName,
              stateColor: state.stateColor,
              long: state.long
            });
          }
        });
      }

      // 2. Check-in
      if (this.service.evidencias) {
        const checkInEvidence = this.service.evidencias.find(ev => ev.check_in === true || ev.type === 'check_in');
        if (checkInEvidence) {
          const date = this.parseServiceDate(checkInEvidence.created_at);
          if (date) {
            events.push({
              date,
              type: 'checkin',
              time: this.formatTime(checkInEvidence.created_at)
            });
          }
        }

        // 3. Evidencias (type = evidence)
        const evidenceList = this.service.evidencias
          .filter(ev => ev.type === 'evidence' || (ev.check_in === false && !ev.type));
        evidenceList.forEach(ev => {
          if (ev.evidence && ev.evidence[0]) {
            const date = this.parseServiceDate(ev.created_at);
            if (date) {
              events.push({
                date,
                type: 'evidence',
                ev
              });
            }
          }
        });
      }

      // Ordenar todos los eventos por fecha ascendente
      events.sort((a, b) => a.date - b.date);

      // Construir bitacoraItems en orden
      events.forEach(event => {
        if (event.type === 'wfstate') {
          const time = moment(event.date).format('HH:mm');
          let icon = 'cilMediaPlay';
          let iconClass = 'bitacora-icon-success';
          let emoji = '🚶';
          if (event.stateId === 34) {
            icon = 'cilMediaStop';
            iconClass = 'bitacora-icon-danger';
            emoji = '🏁';
          } else if (event.stateId === 134) {
            icon = 'cilCloud';
            iconClass = 'bitacora-icon-info';
            emoji = '🌧️';
          }
          this.bitacoraItems.push({
            time,
            icon,
            iconClass,
            label: event.stateName + (event.long ? ` (${event.long})` : ''),
            emoji,
            isWfState: true
          });
        } else if (event.type === 'checkin') {
          this.bitacoraItems.push({
            time: event.time,
            icon: 'cilCheck',
            iconClass: 'bitacora-icon-success',
            label: 'Check-in realizado',
            emoji: '✅',
            isCheckIn: true
          });
        } else if (event.type === 'evidence') {
          const evidence = event.ev.evidence[0];
          const time = this.formatTime(event.ev.created_at);
          let icon = 'cilClock';
          let iconClass = 'bitacora-icon-info';
          let emoji = '';
          let videoLink = null;
          if (evidence.label && evidence.label.toLowerCase) {
            if (evidence.label.toLowerCase().includes('comida')) {
              emoji = '🍽️';
            } else if (evidence.label.toLowerCase().includes('juego') || evidence.label.toLowerCase().includes('jardín')) {
              iconClass = 'bitacora-icon-warning';
              emoji = '🎾';
              videoLink = evidence.photo;
            }
          }
          const hasVideo = !!evidence.video;
          const hasPhoto = !!evidence.photo && !hasVideo;
          this.bitacoraItems.push({
            time,
            icon,
            iconClass,
            label: evidence.label || 'Evidencia',
            emoji,
            videoLink: hasVideo || videoLink ? true : false,
            videoUrl: hasVideo ? evidence.video : (evidence.photo || null),
            hasPhoto,
            photoUrl: evidence.photo
          });
        }
      });
    },
    parseServiceDate(dateString) {
      if (!dateString) return null;
      try {
        const m = moment(dateString, 'DD/MM/YYYY HH:mm:ss', true);
        if (m.isValid()) return m.toDate();
        return moment(dateString).toDate();
      } catch (e) {
        return moment(dateString).toDate();
      }
    },
    buildGallery() {
      // Obtener todas las evidencias con foto o video, ordenadas asc por fecha
      const evidenceWithMedia = this.service.evidencias
        .filter(ev => {
          if (ev.type === 'evidence' || (ev.check_in === false && !ev.type)) {
            return ev.evidence && ev.evidence.some(e => e.photo || e.video);
          }
          return false;
        })
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

      this.galleryItems = [];
      evidenceWithMedia.forEach(ev => {
        if (ev.evidence) {
          ev.evidence.forEach(e => {
            // Si hay video, agregarlo a la galería
            if (e.video) {
              this.galleryItems.push({
                label: e.label || 'Evidencia',
                photo: e.video, // Usar video como URL para la imagen
                video: e.video, // Guardar también la URL del video
                isVideo: true,
                date: ev.created_at
              });
            }
            // Si hay foto, agregarlo a la galería
            else if (e.photo) {
              this.galleryItems.push({
                label: e.label || 'Evidencia',
                photo: e.photo,
                video: null,
                isVideo: false,
                date: ev.created_at
              });
            }
          });
        }
      });
    },
    formatTime(dateString) {
      if (!dateString) return '';
      // Formato: "03/12/2025 11:41:38" -> "14:00"
      try {
        const momentDate = moment(dateString, 'DD/MM/YYYY HH:mm:ss');
        return momentDate.format('HH:mm');
      } catch (error) {
        return dateString;
      }
    },
    formatDate(dateString) {
      if (!dateString) return '';
      try {
        const momentDate = moment(dateString, 'DD/MM/YYYY HH:mm:ss');
        return momentDate.format('DD/MM/YYYY HH:mm');
      } catch (error) {
        return dateString;
      }
    },
    formatDateShort(dateString) {
      if (!dateString) return '';
      try {
        const momentDate = moment(dateString, 'DD/MM/YYYY HH:mm:ss');
        return momentDate.format('DD/MM/YYYY HH:mm');
      } catch (error) {
        return dateString;
      }
    },
    formatScore(score) {
      if (score === null || score === undefined) return '';
      const numScore = parseFloat(score);
      if (isNaN(numScore)) return '';
      // Si el decimal es 0, mostrar solo el entero
      if (numScore % 1 === 0) {
        return numScore.toString();
      }
      // Si tiene decimales, mostrar con un decimal
      return numScore.toFixed(1);
    },
    openVideo(videoUrl) {
      if (videoUrl) {
        window.open(videoUrl, '_blank');
      }
    },
    openImageFullscreen(imageUrl, label = null) {
      // Crear un contenedor fullscreen
      const fullscreenContainer = document.createElement('div');
      fullscreenContainer.className = 'native-fullscreen-container';
      fullscreenContainer.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: rgba(0, 0, 0, 0.98);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        z-index: 9999;
        cursor: pointer;
      `;

      // Botón cerrar
      const closeBtn = document.createElement('button');
      closeBtn.innerHTML = '✕';
      closeBtn.style.cssText = `
        position: absolute;
        top: 20px;
        right: 20px;
        background: rgba(0, 0, 0, 0.7);
        color: white;
        border: none;
        border-radius: 50%;
        width: 45px;
        height: 45px;
        font-size: 24px;
        cursor: pointer;
        z-index: 10000;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.2s ease;
      `;
      closeBtn.onmouseenter = () => {
        closeBtn.style.background = 'rgba(0, 0, 0, 0.9)';
      };
      closeBtn.onmouseleave = () => {
        closeBtn.style.background = 'rgba(0, 0, 0, 0.7)';
      };

      // Imagen
      const img = document.createElement('img');
      img.src = imageUrl;
      img.style.cssText = `
        max-width: 100%;
        max-height: calc(100vh - 100px);
        object-fit: contain;
        cursor: default;
      `;
      img.onclick = (e) => e.stopPropagation();

      // Label si existe
      let labelDiv = null;
      if (label) {
        labelDiv = document.createElement('div');
        labelDiv.textContent = label;
        labelDiv.style.cssText = `
          position: absolute;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(0, 0, 0, 0.7);
          color: white;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          text-align: center;
          max-width: 80%;
        `;
      }

      // Logo watermark en la parte inferior derecha
      const logoWatermark = document.createElement('img');
      logoWatermark.src = require('@/assets/images/logo.png');
      logoWatermark.style.cssText = `
        position: absolute;
        bottom: 20px;
        right: 20px;
        width: 120px;
        height: auto;
        opacity: 0.85;
        z-index: 10000;
        pointer-events: none;
        filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.5));
      `;

      // Función para cerrar
      const close = () => {
        if (document.body.contains(fullscreenContainer)) {
          document.body.removeChild(fullscreenContainer);
        }
        document.body.style.overflow = '';
        // Remover event listener de teclado
        document.removeEventListener('keydown', escapeHandler);
      };

      // Handler para ESC
      const escapeHandler = (e) => {
        if (e.key === 'Escape') {
          close();
        }
      };

      // Event listeners
      closeBtn.onclick = (e) => {
        e.stopPropagation();
        close();
      };
      
      fullscreenContainer.onclick = (e) => {
        if (e.target === fullscreenContainer) {
          close();
        }
      };

      document.addEventListener('keydown', escapeHandler);

      // Agregar al DOM
      fullscreenContainer.appendChild(closeBtn);
      fullscreenContainer.appendChild(img);
      if (labelDiv) {
        fullscreenContainer.appendChild(labelDiv);
      }
      fullscreenContainer.appendChild(logoWatermark);
      document.body.appendChild(fullscreenContainer);
      document.body.style.overflow = 'hidden';
    },
    openVideoFullscreen(videoUrl, label = null) {
      // Crear un contenedor fullscreen
      const fullscreenContainer = document.createElement('div');
      fullscreenContainer.className = 'native-fullscreen-container';
      fullscreenContainer.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: rgba(0, 0, 0, 0.98);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        z-index: 9999;
        cursor: pointer;
      `;

      // Botón cerrar
      const closeBtn = document.createElement('button');
      closeBtn.innerHTML = '✕';
      closeBtn.style.cssText = `
        position: absolute;
        top: 20px;
        right: 20px;
        background: rgba(0, 0, 0, 0.7);
        color: white;
        border: none;
        border-radius: 50%;
        width: 45px;
        height: 45px;
        font-size: 24px;
        cursor: pointer;
        z-index: 10000;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.2s ease;
      `;
      closeBtn.onmouseenter = () => {
        closeBtn.style.background = 'rgba(0, 0, 0, 0.9)';
      };
      closeBtn.onmouseleave = () => {
        closeBtn.style.background = 'rgba(0, 0, 0, 0.7)';
      };

      // Video
      const video = document.createElement('video');
      video.src = videoUrl;
      video.controls = true;
      video.style.cssText = `
        max-width: 100%;
        max-height: calc(100vh - 100px);
        object-fit: contain;
        cursor: default;
      `;
      video.onclick = (e) => e.stopPropagation();

      // Label si existe
      let labelDiv = null;
      if (label) {
        labelDiv = document.createElement('div');
        labelDiv.textContent = label;
        labelDiv.style.cssText = `
          position: absolute;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(0, 0, 0, 0.7);
          color: white;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          text-align: center;
          max-width: 80%;
        `;
      }

      // Logo watermark en la parte inferior derecha
      const logoWatermark = document.createElement('img');
      logoWatermark.src = require('@/assets/images/logo.png');
      logoWatermark.style.cssText = `
        position: absolute;
        bottom: 20px;
        right: 20px;
        width: 120px;
        height: auto;
        opacity: 0.85;
        z-index: 10000;
        pointer-events: none;
        filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.5));
      `;

      // Función para cerrar
      const close = () => {
        if (document.body.contains(fullscreenContainer)) {
          document.body.removeChild(fullscreenContainer);
        }
        document.body.style.overflow = '';
        // Remover event listener de teclado
        document.removeEventListener('keydown', escapeHandler);
      };

      // Handler para ESC
      const escapeHandler = (e) => {
        if (e.key === 'Escape') {
          close();
        }
      };

      // Event listeners
      closeBtn.onclick = (e) => {
        e.stopPropagation();
        close();
      };
      
      fullscreenContainer.onclick = (e) => {
        if (e.target === fullscreenContainer) {
          close();
        }
      };

      document.addEventListener('keydown', escapeHandler);

      // Agregar al DOM
      fullscreenContainer.appendChild(closeBtn);
      fullscreenContainer.appendChild(video);
      if (labelDiv) {
        fullscreenContainer.appendChild(labelDiv);
      }
      fullscreenContainer.appendChild(logoWatermark);
      document.body.appendChild(fullscreenContainer);
      document.body.style.overflow = 'hidden';
    },
    openCheckInDetail() {
      this.showCheckInDetailModal = true;
    },
    toggleFabMenu() {
      this.fabMenuOpen = !this.fabMenuOpen;
    },
    handleFabAction(action) {
      this.fabMenuOpen = false;
      if (action === 'refrescar') {
        this.refreshService();
      } else if (action === 'whatsapp') {
        this.contactWalkerWhatsApp();
      }
    },
    getRefreshButtonBottom() {
      // Refrescar siempre está más arriba (último)
      return '140px';
    },
    getWhatsAppButtonBottom() {
      // WhatsApp está más abajo que refrescar
      return '200px';
    },
    contactWalkerWhatsApp() {
      if (!this.service || !this.service.walkerPhone) {
        this.$bvToast.toast('Número de teléfono del hospedador no disponible', {
          title: 'Error',
          variant: 'warning',
          solid: true
        });
        return;
      }

      // Formatear el número de teléfono (remover espacios y caracteres especiales)
      let phone = this.service.walkerPhone.replace(/\s+/g, '').replace(/[^\d]/g, '');
      
      // Si no empieza con código de país, agregar el código de Colombia (57)
      if (!phone.startsWith('57')) {
        phone = '57' + phone;
      }

      // Crear el mensaje claro indicando que se contacta al hospedador directamente
      const walkerName = this.service.walkerName || 'Hospedador';
      const petName = this.service.mascota || 'mi mascota';
      const message = encodeURIComponent(
        `Hola ${walkerName}, soy el dueño de ${petName} y te contacto directamente sobre el servicio de guardería.`
      );

      const whatsappUrl = `https://api.whatsapp.com/send?phone=${phone}&text=${message}`;
      
      window.open(whatsappUrl, '_blank');
    }
  }
}
</script>

<style scoped>
.guarderia-detail {
  min-height: 100vh;
  background: #f8f9fa;
}

.guarderia-container {
  width: 100%;
}


.guarderia-main-grid {
  animation: fadeInUp 0.6s ease-out 0.2s both;
}

/* Pet + Status Combined Card */
.pet-status-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  height: 100%;
}

.pet-status-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
}

.pet-status-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 1rem 1.25rem;
  color: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.pet-status-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  flex: 1;
}

.refresh-header-btn {
  color: #fff !important;
  padding: 0.25rem 0.5rem;
  margin-left: 0.5rem;
  opacity: 0.9;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.refresh-header-btn:hover:not(:disabled) {
  opacity: 1;
  transform: rotate(90deg);
}

.refresh-header-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.refresh-header-btn .spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.pet-status-body {
  padding: 1.25rem;
}

/* Walker Info Section */
.walker-info-section {
  width: 100%;
}

.walker-info-card {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid #e0e0e0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.walker-info-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.walker-info-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.walker-photo-container {
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  background: #fff;
}

.walker-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.walker-photo-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e9ecef;
}

.walker-details {
  flex: 1;
  min-width: 0;
}

.walker-label {
  font-size: 0.75rem;
  color: #6c757d;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
}

.walker-name {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  word-wrap: break-word;
  line-height: 1.3;
  margin-bottom: 0.5rem;
}

.walker-rating {
  margin-top: 0.5rem;
}

.walker-rating-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.walker-rating-stars {
  display: inline-flex;
  background: transparent !important;
}

.walker-rating-stars .b-rating {
  background: transparent !important;
}

.walker-rating-stars .b-rating-star {
  font-size: 0.875rem;
  padding: 0 0.125rem;
  background: transparent !important;
}

.walker-rating-stars .b-rating-star-wrapper {
  background: transparent !important;
}

.walker-rating-score {
  font-size: 0.875rem;
  font-weight: 600;
  color: #2c3e50;
}

.walker-rating-count {
  font-size: 0.75rem;
  color: #6c757d;
}

.pet-photo-section {
  width: 100%;
}

.pet-photo-container {
  position: relative;
  width: 100%;
  height: 280px;
  overflow: hidden;
  border-radius: 12px;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pet-photo-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  transition: transform 0.3s ease;
}

.pet-photo-container:hover .pet-photo-img {
  transform: scale(1.05);
}

.pet-photo-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
  padding: 2rem 1.5rem 1.5rem;
  color: #fff;
}

.pet-names {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.pet-status-badge {
  display: inline-block;
  background: rgba(255, 193, 7, 0.9);
  color: #000;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
}

.pet-photo-placeholder {
  height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border-radius: 12px;
}

/* Status Section */
.status-section {
  border-top: 1px solid #e0e0e0;
  padding-top: 1rem;
}

.status-header-small {
  margin-bottom: 0.75rem;
}

.status-title-small {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.status-banner-small {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: #fff;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  box-shadow: 0 2px 10px rgba(245, 87, 108, 0.3);
}

.status-emoji-small {
  font-size: 1.25rem;
}

.status-text-small {
  flex: 1;
}

.safe-zone-badge-small {
  background: #4caf50;
  color: #fff;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  box-shadow: 0 2px 10px rgba(76, 175, 80, 0.3);
}

.map-placeholder-small {
  height: 250px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border-radius: 8px;
}

.map-container {
  position: relative;
}

/* Bitácora Card */
.bitacora-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  animation: fadeInUp 0.6s ease-out 0.4s both;
}

.bitacora-header {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  padding: 1rem 1.25rem;
  color: #fff;
}

.bitacora-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
}

.bitacora-body {
  padding: 1.25rem;
  max-height: 600px;
  overflow-y: auto;
}

.bitacora-timeline {
  position: relative;
  padding-left: 0;
}

.bitacora-item {
  position: relative;
  padding-bottom: 2rem;
  padding-left: 4rem;
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
}

.bitacora-item:not(:last-child)::before {
  content: '';
  position: absolute;
  left: 1.25rem;
  top: 2.5rem;
  bottom: -2rem;
  width: 2px;
  background: #e0e0e0;
}

.bitacora-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  position: absolute;
  left: 0;
}

.bitacora-content {
  flex: 1;
  padding-top: 0;
}

.bitacora-time {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
  white-space: nowrap;
  margin-bottom: 0.25rem;
}

.bitacora-icon-success {
  background: #4caf50;
  color: #fff;
}

.bitacora-icon-info {
  background: #2196f3;
  color: #fff;
}

.bitacora-icon-warning {
  background: #ff9800;
  color: #fff;
}

.bitacora-content {
  flex: 1;
  padding-top: 0.25rem;
}

.bitacora-label {
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  line-height: 1.5;
}

.bitacora-emoji {
  font-size: 1.25rem;
  display: inline-block;
  line-height: 1;
}

.bitacora-link a {
  color: #2196f3;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
}

.bitacora-link a:hover {
  color: #1976d2;
  text-decoration: underline;
}

.bitacora-checkin-link {
  color: #2196f3;
  text-decoration: none;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s ease;
}

.bitacora-checkin-link:hover {
  color: #1976d2;
  text-decoration: underline;
}

.bitacora-photo-link {
  color: #2196f3;
  text-decoration: none;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s ease;
  display: inline-flex;
  align-items: center;
}

.bitacora-photo-link:hover {
  color: #1976d2;
  text-decoration: underline;
}

.bitacora-empty {
  text-align: center;
  padding: 3rem;
}

/* Gallery Card */
.gallery-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  animation: fadeInUp 0.6s ease-out 0.6s both;
}

.gallery-header {
  background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
  padding: 1rem 1.25rem;
  color: #fff;
}

.gallery-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
}

.gallery-body {
  padding: 1.25rem;
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
}

.gallery-item {
  position: relative;
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.gallery-item:hover {
  transform: translateY(-5px) scale(1.02);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

.gallery-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.gallery-play-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
}

.gallery-video-label {
  margin-top: 0.5rem;
  font-weight: 600;
}

.gallery-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.gallery-item:hover .gallery-overlay {
  opacity: 1;
}

.gallery-empty {
  text-align: center;
  padding: 3rem;
}

/* Check-in Detail Modal */
.checkin-modal-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border-bottom: none;
}

.checkin-modal-body {
  padding: 1.5rem;
}

.checkin-detail-items {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.checkin-detail-item {
  width: 100%;
}

.checkin-detail-card {
  background-color: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 1rem;
}

.checkin-detail-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.checkin-detail-left {
  flex: 1;
}

.checkin-detail-label {
  font-size: 1rem;
  color: #333;
}

.checkin-detail-right {
  flex-shrink: 0;
}

.checkin-detail-photo-thumbnail {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #e0e0e0;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.checkin-detail-photo-thumbnail:hover {
  transform: scale(1.05);
}

.checkin-thumbnail-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.checkin-thumbnail-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.checkin-detail-photo-thumbnail:hover .checkin-thumbnail-overlay {
  opacity: 1;
}

.checkin-detail-no-photo {
  width: 80px;
  height: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background-color: #f5f5f5;
  border: 2px dashed #e0e0e0;
}


/* Animations */
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media (max-width: 992px) {
  .pet-photo-container,
  .pet-photo-placeholder {
    height: 250px;
  }

  .bitacora-body {
    max-height: none;
  }

  .walker-info-card {
    padding: 0.875rem;
  }

  .walker-photo-container {
    width: 50px;
    height: 50px;
  }

  .walker-name {
    font-size: 0.9rem;
  }
}

@media (max-width: 768px) {
  .guarderia-detail {
    padding: 1rem 0.5rem;
  }

  .pet-photo-container,
  .pet-photo-placeholder {
    height: 300px;
  }

  .gallery-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 0.75rem;
  }

  .bitacora-timeline {
    padding-left: 2.5rem;
  }

  .walker-info-content {
    gap: 0.75rem;
  }

  .walker-photo-container {
    width: 48px;
    height: 48px;
  }

  .walker-label {
    font-size: 0.7rem;
  }

  .walker-name {
    font-size: 0.85rem;
  }
}

@media (max-width: 576px) {

  .status-card-body,
  .bitacora-body,
  .gallery-body {
    padding: 1rem;
  }

  .gallery-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* FAB Container */
.fab-container {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
}

/* Overlay para cerrar el menú */
.fab-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 999;
}

/* Botones FAB */
.fab-button {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 1001;
}

.fab-button.primary {
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.4);
}

.fab-button.secondary {
  width: 48px;
  height: 48px;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.3);
}

.fab-button:hover:not(:disabled) {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.5);
  transform: scale(1.1);
}

.fab-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Contenedor de items FAB */
.fab-items {
  position: absolute;
  bottom: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

/* Item individual del FAB */
.fab-item {
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  z-index: 1001;
}

/* Label del FAB */
.fab-label {
  background-color: rgba(0, 0, 0, 0.87);
  color: #fff;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 14px;
  margin-right: 12px;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* Transiciones para los items */
.fab-item-enter-active,
.fab-item-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.fab-item-enter {
  opacity: 0;
  transform: translateY(20px) scale(0.8);
}

.fab-item-enter-to {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.fab-item-leave {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.fab-item-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.8);
}
</style>

